/**
 * Template de prise de besoin : Procédure d'escalade v1
 * Aligné sur ITIL 4, COBIT 2019 et ISO 27001
 * Procédure opérationnelle détaillée pour l'escalade des incidents
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateProcedureEscaladeV1: IntakeTemplate = {
  id: 'procedure-escalade-v1',
  name: 'Prise de besoin Procédure d\'escalade v1',
  description:
    'Questionnaire complet pour élaborer une procédure d\'escalade conforme ITIL 4, COBIT 2019 et ISO 27001. Définit les critères, délais, responsables et workflows pour l\'escalade hiérarchique et fonctionnelle des incidents.',
  documentTypeCode: 'ESCALATION_PROCEDURE',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Structure d\'Escalade',
      description: 'Structure et niveaux d\'escalade',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la procédure.',
        },
        {
          id: 'niveaux_escalade',
          label: 'Combien de niveaux d\'escalade avez-vous ?',
          type: 'select',
          options: ['2 niveaux', '3 niveaux', '4 niveaux', '5 niveaux', 'À déterminer'],
          required: true,
          helpText: 'ITIL 4: Structure d\'escalade pour gestion efficace.',
        },
        {
          id: 'type_escalade',
          label: 'Quel type d\'escalade utilisez-vous ?',
          type: 'select',
          options: [
            'Escalade hiérarchique uniquement',
            'Escalade fonctionnelle uniquement',
            'Escalade hybride (hiérarchique + fonctionnelle)',
            'À déterminer',
          ],
          required: true,
          helpText: 'ITIL 4: Type d\'escalade pour gestion appropriée.',
        },
        {
          id: 'responsables_niveaux',
          label: 'Qui sont les responsables à chaque niveau d\'escalade ?',
          type: 'textarea',
          placeholder: 'Décrivez: Niveau 1 → Techniciens, Niveau 2 → Gestionnaires, Niveau 3 → Experts, Niveau 4 → Direction...',
          required: true,
          helpText: 'ITIL 4: Responsables clairs pour chaque niveau.',
        },
        {
          id: 'ton_document_procedure',
          label: 'Quel ton souhaitez-vous pour cette procédure ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation de la procédure.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Critères & Délais d\'Escalade',
      description: 'Critères et délais d\'escalade',
      icon: 'Clock',
      questions: [
        {
          id: 'criteres_escalade',
          label: 'Quels sont les critères d\'escalade ?',
          type: 'multiselect',
          options: [
            'Délai de résolution dépassé',
            'Impact business critique',
            'Nombre d\'utilisateurs affectés',
            'Criticité du service',
            'Demande explicite du client',
            'Incident récurrent',
            'Compétence insuffisante',
            'Ressources insuffisantes',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Critères pour décision d\'escalade.',
        },
        {
          id: 'delais_escalade',
          label: 'Quels sont les délais d\'escalade par niveau et priorité ?',
          type: 'textarea',
          placeholder: 'Ex: P1 → Niveau 1: 30 min, Niveau 2: 1h, Niveau 3: 2h\nP2 → Niveau 1: 2h, Niveau 2: 4h...',
          helpText: 'ITIL 4: Délais pour escalade rapide.',
        },
        {
          id: 'escalade_automatique',
          label: 'Y a-t-il des escalades automatiques ?',
          type: 'select',
          options: ['Oui, pour incidents critiques', 'Oui, pour tous les incidents', 'Non', 'À déterminer'],
          helpText: 'ITIL 4: Escalade automatique pour réponse rapide.',
        },
        {
          id: 'processus_escalade',
          label: 'Quel est le processus d\'escalade ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Détection critère → Notification responsable → Attribution → Suivi...',
          helpText: 'ITIL 4: Processus structuré pour escalade efficace.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Communication & Documentation',
      description: 'Communication et documentation d\'escalade',
      icon: 'MessageSquare',
      questions: [
        {
          id: 'communication_escalade',
          label: 'Comment communique-t-on lors d\'une escalade ?',
          type: 'multiselect',
          options: [
            'Notification automatique par système',
            'Appel téléphonique',
            'Courriel',
            'Message Teams / Slack',
            'Réunion d\'urgence',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Communication pour escalade efficace.',
        },
        {
          id: 'templates_notification',
          label: 'Quels templates de notification sont utilisés ?',
          type: 'textarea',
          placeholder: 'Décrivez les templates: Notification escalade niveau 2, Notification escalade niveau 3...',
          helpText: 'ITIL 4: Templates pour communication cohérente.',
        },
        {
          id: 'documentation_escalade',
          label: 'Quelle documentation est créée lors d\'une escalade ?',
          type: 'multiselect',
          options: [
            'Journal d\'escalade',
            'Rapport d\'escalade',
            'Notification aux parties prenantes',
            'Mise à jour du ticket',
            'Documentation des actions prises',
            'Autre',
          ],
          helpText: 'ITIL 4: Documentation pour traçabilité.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Amélioration Continue',
      description: 'Amélioration continue de la procédure',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'metriques_escalade',
          label: 'Quelles métriques d\'escalade sont suivies ?',
          type: 'multiselect',
          options: [
            'Nombre d\'escalades par niveau',
            'Temps moyen avant escalade',
            'Taux d\'escalade par priorité',
            'Taux de résolution après escalade',
            'Satisfaction après escalade',
            'Autre',
          ],
          helpText: 'ITIL 4: Métriques pour mesurer la performance.',
        },
        {
          id: 'revision_procedure',
          label: 'À quelle fréquence souhaitez-vous réviser cette procédure ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





